import { useState, useEffect } from 'react';
import { Clock, TrendingUp, AlertTriangle, Moon, Sun } from 'lucide-react';
import { analyzeSleepPatterns, formatPatternTime, calculateAge } from '../utils/dateUtils';

export const SleepPatterns = ({ entries, child, onUpdatePatterns }) => {
  const [patterns, setPatterns] = useState(null);
  const [excludeSpecialEvents, setExcludeSpecialEvents] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entries && entries.length > 0) {
      const analyzedPatterns = analyzeSleepPatterns(entries, excludeSpecialEvents);
      setPatterns(analyzedPatterns);
    } else {
      setPatterns(null);
    }
  }, [entries, excludeSpecialEvents]);

  const handleUpdatePatterns = async () => {
    if (!patterns || !child) return;
    
    setLoading(true);
    try {
      await onUpdatePatterns(child.id, 'bedtime', patterns.bedtime, patterns.confidence);
      await onUpdatePatterns(child.id, 'wakeTime', patterns.wakeTime, patterns.confidence);
    } catch (error) {
      console.error('Error updating patterns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAgeDisplay = () => {
    if (!child || !child.birthDate) return null;
    const age = calculateAge(child.birthDate);
    return age.display;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600 dark:text-green-400';
    if (confidence >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 80) return 'Sehr zuverlässig';
    if (confidence >= 60) return 'Zuverlässig';
    if (confidence >= 40) return 'Mittel';
    return 'Niedrig';
  };

  if (!child) {
    return (
      <div className="mobile-card text-center py-8">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Kein Kind ausgewählt
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Wähle ein Kind aus, um Schlafmuster zu analysieren.
        </p>
      </div>
    );
  }

  if (!patterns) {
    return (
      <div className="mobile-card text-center py-8">
        <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Keine Daten verfügbar
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Füge Schlaf-Einträge hinzu, um Muster zu erkennen.
        </p>
      </div>
    );
  }

  return (
    <div className="mobile-space-y">
      <div className="flex items-center justify-between">
        <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">
          Schlafmuster
        </h3>
        
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={excludeSpecialEvents}
              onChange={(e) => setExcludeSpecialEvents(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-gray-600 dark:text-gray-400">Ausnahmen ausschließen</span>
          </label>
        </div>
      </div>

      {/* Child Info */}
      <div className="mobile-card bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              {child.name}
            </h4>
            {getAgeDisplay() && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {getAgeDisplay()} alt
              </p>
            )}
          </div>
          <button
            onClick={handleUpdatePatterns}
            disabled={loading}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Aktualisiere...' : 'Muster speichern'}
          </button>
        </div>
      </div>

      {/* Pattern Display */}
      <div className="mobile-grid">
        {/* Bedtime Pattern */}
        <div className="mobile-card">
          <div className="flex items-center space-x-2 mb-3">
            <Moon className="w-5 h-5 text-blue-500" />
            <h4 className="font-medium text-gray-900 dark:text-white">Einschlafzeit</h4>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {patterns.bedtime ? formatPatternTime(patterns.bedtime) : '--:--'}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Durchschnittliche Einschlafzeit
            </div>
          </div>
        </div>

        {/* Wake Time Pattern */}
        <div className="mobile-card">
          <div className="flex items-center space-x-2 mb-3">
            <Sun className="w-5 h-5 text-yellow-500" />
            <h4 className="font-medium text-gray-900 dark:text-white">Aufwachzeit</h4>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {patterns.wakeTime ? formatPatternTime(patterns.wakeTime) : '--:--'}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Durchschnittliche Aufwachzeit
            </div>
          </div>
        </div>

        {/* Confidence */}
        <div className="mobile-card">
          <div className="flex items-center space-x-2 mb-3">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <h4 className="font-medium text-gray-900 dark:text-white">Zuverlässigkeit</h4>
          </div>
          
          <div className="text-center">
            <div className={`text-2xl font-bold mb-1 ${getConfidenceColor(patterns.confidence)}`}>
              {patterns.confidence}%
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {getConfidenceLabel(patterns.confidence)}
            </div>
          </div>
        </div>

        {/* Sample Size */}
        <div className="mobile-card">
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="w-5 h-5 text-purple-500" />
            <h4 className="font-medium text-gray-900 dark:text-white">Datenbasis</h4>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {patterns.sampleSize}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Einträge analysiert
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="mobile-card">
        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
          Erkenntnisse
        </h4>
        
        <div className="space-y-3">
          {patterns.confidence < 50 && (
            <div className="flex items-start space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Niedrige Zuverlässigkeit:</strong> Das Schlafmuster ist noch nicht konsistent genug. 
                Füge mehr Einträge hinzu, um bessere Vorhersagen zu erhalten.
              </div>
            </div>
          )}

          {patterns.bedtime && patterns.wakeTime && (
            <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Schlafdauer:</strong> Durchschnittlich schläft {child.name} etwa{' '}
                {Math.round((patterns.wakeTime - patterns.bedtime) / (1000 * 60 * 60))} Stunden pro Nacht.
              </div>
            </div>
          )}

          {excludeSpecialEvents && (
            <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800 dark:text-green-200">
                <strong>Bereinigt:</strong> Besondere Ereignisse (Urlaub, Krankheit, etc.) werden aus der Analyse ausgeschlossen 
                für genauere Muster.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 